import {
    Copy,
    Download,
    ExternalLink,
    Eye,
    FileArchive,
    FileUp,
    FolderInput,
    FolderOpen,
    FolderPlus,
    FolderUp,
    History,
    Inbox,
    Info,
    Link2,
    Lock,
    MessageSquarePlus,
    PackageOpen,
    Pencil,
    RotateCcw,
    Share2,
    Star,
    StarOff,
    Trash2
} from "lucide-react";
import type { MenuEntry } from "@/components/ui/menu";
import type { DriveItem } from "@/api/driveApi";
import { OFFICE_APP_NAME, categoryOf, extensionOf, officeKindOf, openWithAppName } from "@/lib/fileTypes";
import type { DriveActions } from "../driveActions";
import type { UploadsApi } from "../upload/UploadProvider";
import type { DriveVariant } from "../types";

const sep = (id: string): MenuEntry => ({ kind: "separator", id });

// Someone else's item shared with the user carries ownerEmail + role. Only
// the owner stars, moves, bins, extracts or protects; editors rename, share
// and see history; downloading (and so copying) can be blocked for viewers.
export const owns = (i: DriveItem) => !i.ownerEmail;
export const canEdit = (i: DriveItem) => owns(i) || i.role === "editor";
export const canDownload = (i: DriveItem) => owns(i) || i.canDownload !== false;

/** Context / ⋯ menu for one or more items. */
export function buildItemMenu(
    targets: DriveItem[],
    o: {
        variant: DriveVariant;
        actions: DriveActions;
        list: DriveItem[];
        onDetails: (item: DriveItem) => void;
        /** Opens the details pane on its Comments tab. */
        onComment?: (item: DriveItem) => void;
        zipParentId: string | null;
    }
): MenuEntry[] {
    const a = o.actions;
    const single = targets.length === 1 ? targets[0] : undefined;

    if (o.variant === "trash") {
        const entries: MenuEntry[] = [{ id: "restore", label: "Restore", icon: RotateCcw, onSelect: () => void a.restore(targets) }];
        if (single) entries.push({ id: "details", label: "Details", icon: Info, onSelect: () => o.onDetails(single) });
        entries.push(sep("s1"), {
            id: "delete",
            label: "Delete forever",
            icon: Trash2,
            shortcut: "Del",
            danger: true,
            onSelect: () => void a.deleteForever(targets)
        });
        return entries;
    }

    const allStarred = targets.every((t) => t.isFavorite);
    const star: MenuEntry = {
        id: "star",
        label: allStarred ? "Remove from Starred" : "Add to Starred",
        icon: allStarred ? StarOff : Star,
        onSelect: () => a.toggleStar(targets)
    };
    const entries: MenuEntry[] = [];

    if (single) {
        const office = officeKindOf(single);
        if (single.kind === "Folder") {
            entries.push({ id: "open", label: "Open", icon: FolderOpen, shortcut: "Enter", onSelect: () => a.open(single) });
        } else {
            if (office) {
                entries.push({
                    id: "open-app",
                    label: `Open in ${OFFICE_APP_NAME[office]}`,
                    icon: ExternalLink,
                    shortcut: "Enter",
                    onSelect: () => a.openInApp(single)
                });
            }
            entries.push({ id: "preview", label: "Preview", icon: Eye, shortcut: office ? "Space" : "Enter", onSelect: () => a.preview(single, o.list) });
            // PDFs preview in place (Enter) and also open in the PDF editor,
            // which needs the bytes — so not for view-only, no-download shares.
            const app = openWithAppName(single);
            if (!office && app && canDownload(single)) {
                entries.push({ id: "open-app", label: `Open in ${app}`, icon: ExternalLink, onSelect: () => a.openInApp(single) });
            }
        }
        entries.push(sep("s0"));
        if (canEdit(single)) entries.push({ id: "share", label: "Share", icon: Share2, onSelect: () => a.share(single) });
        if (owns(single) && single.kind === "Folder") {
            entries.push({ id: "request-files", label: "Request files", icon: Inbox, onSelect: () => a.requestFiles(single) });
        }
        if (canEdit(single)) entries.push({ id: "copy-link", label: "Copy link", icon: Link2, onSelect: () => void a.copyLink(single) });
        if (canDownload(single)) entries.push({ id: "download", label: "Download", icon: Download, onSelect: () => void a.download([single]) });
        if (canEdit(single)) entries.push({ id: "rename", label: "Rename", icon: Pencil, shortcut: "F2", onSelect: () => void a.rename(single) });
        // A copy of someone else's item lands in the user's own My Drive.
        if (canDownload(single)) {
            entries.push({ id: "copy", label: owns(single) ? "Make a copy" : "Make a copy in My Drive", icon: Copy, onSelect: () => void a.copy([single]) });
        }
        if (owns(single)) entries.push({ id: "move", label: "Move to…", icon: FolderInput, onSelect: () => void a.move([single]) }, star);
        entries.push(sep("s1"));
        if (single.kind === "File" && canEdit(single)) {
            entries.push({ id: "history", label: "Version history", icon: History, onSelect: () => a.history(single) });
        }
        // Anyone with access comments — viewers too (Google's "commenter").
        const onComment = o.onComment;
        if (single.kind === "File" && onComment) {
            entries.push({ id: "comment", label: "Comment", icon: MessageSquarePlus, onSelect: () => onComment(single) });
        }
        entries.push({ id: "details", label: "Details", icon: Info, onSelect: () => o.onDetails(single) });
        if (owns(single) && single.kind === "File" && extensionOf(single.name) === "zip") {
            entries.push({ id: "unzip", label: "Extract here", icon: PackageOpen, onSelect: () => void a.unzip(single) });
        }
        if (owns(single) && single.kind === "File" && categoryOf(single) === "pdf") {
            entries.push({ id: "protect", label: "Protect PDF", icon: Lock, onSelect: () => a.protect(single) });
        }
    } else {
        const allFiles = targets.every((t) => t.kind === "File");
        const allOwned = targets.every(owns);
        if (targets.every(canDownload)) {
            entries.push({ id: "download", label: `Download ${targets.length} items (zip)`, icon: Download, onSelect: () => void a.download(targets) });
        }
        if (allOwned) entries.push({ id: "move", label: "Move to…", icon: FolderInput, onSelect: () => void a.move(targets) });
        if (allFiles && targets.every(canDownload)) entries.push({ id: "copy", label: "Make copies", icon: Copy, onSelect: () => void a.copy(targets) });
        if (allOwned) entries.push(star, { id: "zip", label: "Zip into Drive", icon: FileArchive, onSelect: () => void a.zip(targets, o.zipParentId) });
    }

    if (targets.every(owns)) {
        entries.push(sep("s2"), {
            id: "trash",
            label: "Move to bin",
            icon: Trash2,
            shortcut: "Del",
            danger: true,
            onSelect: () => void a.trash(targets)
        });
    }
    return entries;
}

/** Right-click on empty space inside a folder. */
export function buildCreateMenu(parentId: string | null, actions: DriveActions, uploads: UploadsApi): MenuEntry[] {
    return [
        { id: "new-folder", label: "New folder", icon: FolderPlus, iconClass: "text-amber-500", shortcut: "N", onSelect: () => void actions.newFolder(parentId) },
        sep("c1"),
        { id: "upload-files", label: "Upload files", icon: FileUp, onSelect: () => uploads.pickFiles(parentId) },
        { id: "upload-folder", label: "Upload folder", icon: FolderUp, onSelect: () => uploads.pickFolder(parentId) }
    ];
}

/** Menu on the current-folder breadcrumb: create/upload here, plus folder actions. */
export function buildFolderMenu(
    folder: DriveItem | null,
    parentId: string | null,
    actions: DriveActions,
    uploads: UploadsApi,
    opts: { onDetails: () => void; onTrashed: () => void }
): MenuEntry[] {
    // Someone else's folder: nothing can be created in it (Drive files new
    // items under their owner), and only their own actions apply.
    if (folder && !owns(folder)) {
        const shared: MenuEntry[] = [];
        if (canEdit(folder)) shared.push({ id: "share", label: "Share", icon: Share2, onSelect: () => actions.share(folder) });
        if (canDownload(folder)) shared.push({ id: "download", label: "Download", icon: Download, onSelect: () => void actions.download([folder]) });
        if (canEdit(folder)) shared.push({ id: "rename", label: "Rename", icon: Pencil, onSelect: () => void actions.rename(folder) });
        shared.push({ id: "details", label: "Details", icon: Info, onSelect: opts.onDetails });
        return shared;
    }
    const entries = buildCreateMenu(parentId, actions, uploads);
    if (!folder) return entries;
    entries.push(
        sep("f1"),
        { id: "share", label: "Share", icon: Share2, onSelect: () => actions.share(folder) },
        { id: "request-files", label: "Request files", icon: Inbox, onSelect: () => actions.requestFiles(folder) },
        { id: "download", label: "Download", icon: Download, onSelect: () => void actions.download([folder]) },
        { id: "rename", label: "Rename", icon: Pencil, onSelect: () => void actions.rename(folder) },
        { id: "move", label: "Move to…", icon: FolderInput, onSelect: () => void actions.move([folder]) },
        {
            id: "star",
            label: folder.isFavorite ? "Remove from Starred" : "Add to Starred",
            icon: folder.isFavorite ? StarOff : Star,
            onSelect: () => actions.toggleStar([folder])
        },
        { id: "details", label: "Details", icon: Info, onSelect: opts.onDetails },
        sep("f2"),
        {
            id: "trash",
            label: "Move to bin",
            icon: Trash2,
            danger: true,
            onSelect: () => {
                void actions.trash([folder]).then(opts.onTrashed);
            }
        }
    );
    return entries;
}
